/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useCallback, useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Else, If, Then, Unless, When } from "react-if";
import {
  Button,
  Description,
  Dropdown,
  Input,
  Label,
  Sidebar,
  theme,
} from "@nordcloud/gnui";
import {
  Role,
  UserPermissions,
  useSetUserPermissionsMutation,
} from "~/generated/graphql";
import { Fieldset, LoaderWrap } from "~/components";
import { useCustomers } from "~/services/customers";
import { handleError, isEmpty, isNotNil } from "~/tools";
import { showRole } from "~/views/settings/UsersAndPermissions/showRole";
import { UsersAndPermissionsSchema } from "~/views/settings/validators/usersAndPermissions";
import { ApplicationList } from "./ApplicationList";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  refetch: () => void;
  edit?: boolean;
  editItem?: UserPermissions;
};

export function AddUserPermissionsSidebar({
  isOpen,
  onClose,
  refetch,
  edit,
  editItem,
}: Props) {
  const { isAdminLite } = useCustomers();

  const [userRole, setUserRole] = useState<Role>(
    isAdminLite ? Role.AdminLite : Role.Admin
  );
  const [selectedApplications, setSelectedApplications] = useState<string[]>(
    []
  );

  const [selectedApplicationsList, setSelectedApplicationsList] = useState<
    string[]
  >([]);

  const {
    register,
    handleSubmit,
    setValue,
    reset,

    formState: { errors },
  } = useForm({
    resolver: yupResolver(UsersAndPermissionsSchema),
  });

  const [setUserPermissions, { loading: isSetUserPermissionsLoading }] =
    useSetUserPermissionsMutation();

  const onSubmit = async ({ email }: UserPermissions) => {
    const inputApplications =
      userRole === Role.Admin ? [] : selectedApplicationsList;

    try {
      await setUserPermissions({
        variables: {
          email,
          input: {
            role: userRole,
            applications: inputApplications,
          },
        },
      });

      reset();
      onClose();
      refetch();
    } catch (err) {
      handleError(err);
    }

    setSelectedApplications([]);
    setSelectedApplicationsList([]);
    !edit && setUserRole(Role.Admin);
  };

  const handleClear = useCallback(() => {
    setSelectedApplications([]);
    setSelectedApplicationsList([]);
  }, []);

  useEffect(() => {
    if (edit) {
      const initialSelectedApplications =
        editItem?.access?.applicationsWithNames
          ?.filter(({ name }) => isNotNil(name))
          ?.map(({ nid }) => nid) ?? [];

      setSelectedApplications(initialSelectedApplications);
      setSelectedApplicationsList(initialSelectedApplications);
      setValue("email", editItem?.email);
      setUserRole(
        editItem?.access?.role === "ADMIN" ? Role.Admin : Role.TeamLead
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editItem, edit]);

  useEffect(() => {
    !edit && setUserRole(Role.Admin);

    if (!isOpen) {
      reset();
      setUserRole(
        editItem?.access?.role === "ADMIN" ? Role.Admin : Role.TeamLead
      );
      setSelectedApplications([]);
      setSelectedApplicationsList([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onCloseSidebar = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const isPermissionsRequired = userRole === Role.TeamLead;

  return (
    <>
      <Sidebar
        isOpen={isOpen}
        title={edit ? "Edit permissions" : "Add new User"}
        footer={
          <Button
            m={theme.spacing.spacing04}
            ml="auto"
            type="submit"
            form="addUserForm"
            disabled={isPermissionsRequired && isEmpty(selectedApplications)}
          >
            Apply
          </Button>
        }
        onClick={onCloseSidebar}
      >
        <LoaderWrap inContent loading={isSetUserPermissionsLoading}>
          <form id="addUserForm" onSubmit={handleSubmit(onSubmit)}>
            <Fieldset>
              <Label required name="Email" />
              <If condition={edit}>
                <Then>
                  <Input disabled={edit} defaultValue={editItem?.email} />
                  <input
                    type="hidden"
                    {...register("email", { required: true })}
                    defaultValue={editItem?.email}
                  />
                </Then>
                <Else>
                  <Input
                    type="text"
                    {...register("email", { required: true })}
                    defaultValue=""
                    placeholder="Email"
                  />
                  {errors.email && (
                    <Description>Email is required.</Description>
                  )}
                </Else>
              </If>
            </Fieldset>
            <Unless condition={isAdminLite}>
              <Fieldset>
                <Label required name="User Role" />
                <Dropdown
                  value={`${userRole}`}
                  name="Select User Role"
                  options={userOptions}
                  title="Select User Role"
                  onChange={(newValue) => setUserRole(newValue as Role)}
                />
                {errors.role && <Description>Role is required.</Description>}
              </Fieldset>
            </Unless>
            <When condition={isPermissionsRequired}>
              <ApplicationList
                selectedApplications={selectedApplications}
                setSelectedApplicationsList={setSelectedApplicationsList}
                setSelectedApplications={setSelectedApplications}
                handleClear={handleClear}
              />
            </When>
          </form>
        </LoaderWrap>
      </Sidebar>
    </>
  );
}

const userOptions = [
  { value: Role.Admin, label: showRole(Role.Admin) },
  { value: Role.TeamLead, label: showRole(Role.TeamLead) },
];
